import * as React from "react";
import type { MultiValue } from "react-select";

import { compact, throttle } from "lodash";
import { fetchQuery, graphql } from "relay-runtime";

import { environment } from "~/models";
import AsyncMultiSelector from "~/ui/selectors/AsyncMultiSelector";

import type { MeetingMultiSelectorQuery as QueryType } from "~/__generated__/MeetingMultiSelectorQuery.graphql";

import styles from "./MeetingMultiSelector.module.css";

const { useEffect, useMemo } = React;

export type MeetingType = {
	id: string;
	guid: string;
	title: string;
	start: string | null;
	end: string | null;
};

type SelectorOption = MeetingType & { value: string; label: string };

const MeetingMultiSelectorQuery = graphql`
	query MeetingMultiSelectorQuery($query: String!, $start: DateTime!, $end: DateTime!) {
		events(query: $query, start: $start, end: $end, recurring: true, oneOnOneOnly: true) {
			edges {
				node {
					id
					guid
					title
					start
					end
				}
			}
		}
	}
`;

interface Props {
	onChange: (value: MultiValue<MeetingType>) => void;
	value: MultiValue<MeetingType>;
	start: Date;
	end: Date;
	isDisabled?: boolean;
}

export default function MeetingMultiSelector({ onChange, value, start, end, isDisabled }: Props) {
	const internalValue = useMemo(() => value.map(v => ({ ...v, value: v.guid, label: v.title })), [value]);
	const throttledFetchOptions = useMemo(() => {
		// We don't use an async function/promise here because it interacts in a surprising
		// way with lodash's throttle function. Specifically, functions throttled with
		// lodash will return their most recent value until it runs again, even if the
		// argument has changed. This means that react-select will get stale values for
		// search queries, and cache them. If instead of returning a promise, we use a
		// callback, the callback is only called when the throttled function actually
		// runs, preventing the selector from getting stale values.
		//
		// On top of that, if we use an async function at all, even if it never returns
		// anything, it still returns a Promise<void>. react-select, seeing that we
		// returned a promise, will use promise mode rather than callback mode, meaning
		// it never gets any values at all.
		function fetchOptions(query: string, callback: (options: SelectorOption[]) => void): void {
			fetchQuery<QueryType>(environment, MeetingMultiSelectorQuery, {
				query,
				start: start.toISOString(),
				end: end.toISOString(),
			})
				.toPromise()
				.then(result => {
					if (!result) return;
					const { events } = result;
					const options = compact(
						events.edges.map(edge =>
							edge?.node
								? {
										...edge.node,
										value: edge.node.guid,
										label: edge.node.title,
									}
								: null,
						),
					);
					callback(options);
				});
		}
		return throttle(fetchOptions, 250);
	}, [start, end]);

	useEffect(() => {
		return () => {
			// if throttledFetchOptions changes, or this component unmounts, cancel any
			// pending fetches
			throttledFetchOptions.cancel();
		};
	}, [throttledFetchOptions]);

	return (
		<AsyncMultiSelector<SelectorOption>
			condensed
			width="10rem"
			placeholder="Select meetings"
			cacheOptions
			isClearable
			isMulti
			onChange={onChange}
			value={internalValue}
			loadOptions={throttledFetchOptions}
			isDisabled={isDisabled}
			menuAlign="end"
			menuMaxWidth="20rem"
			className={styles.trigger}
		/>
	);
}
